import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
  Bell, Store, Briefcase, CreditCard, HandCoins, Hourglass, CheckCheck, CheckCircle2,
} from "lucide-react";
import { Button } from "../../components/ui/button";

const TYPE_META = {
  shop_signup: { icon: Store, color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", label: "Shop signup" },
  distributor_signup: { icon: Briefcase, color: "bg-sky-500/15 text-sky-300 border-sky-500/30", label: "Distributor signup" },
  payment_submitted: { icon: CreditCard, color: "bg-amber-500/15 text-amber-300 border-amber-500/30", label: "Payment" },
  commission_pending: { icon: HandCoins, color: "bg-amber-500/15 text-amber-300 border-amber-500/30", label: "Commission" },
  shop_inactive: { icon: Hourglass, color: "bg-red-500/15 text-red-300 border-red-500/30", label: "Inactive" },
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "shop_signup", label: "Shops" },
  { id: "distributor_signup", label: "Distributors" },
  { id: "payment_submitted", label: "Payments" },
  { id: "commission_pending", label: "Commissions" },
  { id: "shop_inactive", label: "Inactive" },
];

export default function AdminNotifications() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => {
    const params = { limit: 300 };
    if (filter === "unread") params.unread_only = true;
    else if (filter !== "all") params.type = filter;
    api.get("/admin/notifications", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const markAllRead = async () => {
    await api.post("/admin/notifications/mark-all-read");
    load();
  };

  const markOne = async (id) => {
    await api.post("/admin/notifications/mark-read", { ids: [id] });
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-notifications-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">Notifications</h1>
          <p className="text-slate-400 mt-1">Everything that needs admin attention — latest first.</p>
        </div>
        <Button onClick={markAllRead} className="bg-slate-800 hover:bg-slate-700 text-slate-100" data-testid="notifications-mark-all">
          <CheckCheck className="w-4 h-4 mr-1.5" /> Mark all read
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`notif-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${filter === f.id ? "bg-amber-500 border-amber-500 text-slate-900" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-[#151A23] border border-slate-800 rounded-md divide-y divide-slate-800">
        {rows.length === 0 && (
          <div className="p-12 text-center text-slate-500">
            <Bell className="w-10 h-10 mx-auto text-slate-600 mb-3" />
            <div>No notifications in this view.</div>
          </div>
        )}
        {rows.map((n) => {
          const meta = TYPE_META[n.type] || { icon: Bell, color: "bg-slate-700 text-slate-300 border-slate-600", label: n.type };
          return (
            <div key={n.id} className="flex items-start gap-4 p-4 hover:bg-slate-800/40" data-testid={`notif-row-${n.id}`}>
              <div className={`w-10 h-10 rounded-md border grid place-items-center ${meta.color}`}>
                <meta.icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{meta.label}</span>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-sky-400" />}
                  <span className="text-[10px] text-slate-500 tabular ml-auto">{(n.created_at || "").slice(0, 19).replace("T", " ")}</span>
                </div>
                <div className="mt-0.5 text-white font-semibold">{n.title}</div>
                {n.body && <div className="text-sm text-slate-400 mt-0.5">{n.body}</div>}
                <div className="mt-2 flex items-center gap-2">
                  {n.link && (
                    <Link to={n.link} className="text-xs font-bold text-sky-300 hover:text-sky-200" data-testid={`notif-open-${n.id}`}>
                      Open →
                    </Link>
                  )}
                  {!n.read && (
                    <button onClick={() => markOne(n.id)} data-testid={`notif-mark-read-${n.id}`} className="text-xs font-semibold text-slate-400 hover:text-white inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Mark read
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
